import * as React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import CustomersTable from 'src/features/customers/components/tables/CustomersTable';
import CustomerDashboard from 'src/features/customers/components/CustomerDashboard';
import HomeLayout from 'src/features/home/components/HomeLayout';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import TaxReasonsTable from 'src/features/taxReasons/components/tables/TaxReasonsTable';
import ServicePlanCategoriesTable from 'src/features/servicePlanCategory/components/tables/ServicePlanCategoriesTable';
import ServicePlansTable from 'src/features/servicePlans/components/tables/ServicePlansTable';
import ServicePlanDashboard from 'src/features/servicePlans/components/ServicePlanDashboard';
import ServicePlanItemDashboard from 'src/features/servicePlanItems/components/ServicePlanItemDashboard';
import ReferenceRatesLayout from 'src/features/referenceRates/components/ReferenceRatesLayout';
import BillableMetricsLayout from 'src/features/billableMetrics/components/BillableMetricsLayout';
import ReferenceRateDashboard from 'src/features/referenceRates/components/ReferenceRateDashboard';
import BillingSimulationPage from 'src/features/billingSimulation/components/BillingSimulationPage';
import BillingEntitiesTable from 'src/features/billing/components/tables/BillingEntitiesTable';
import BillingEntityDashboard from 'src/features/billing/components/BillingEntityDashboard';
import AccountingHeadersTable from 'src/features/accountingHeaders/components/tables/AccountingHeadersTable';
import AccountingRangesTable from 'src/features/accountingRanges/components/tables/AccountingRangesTable';
import AccountingRangeDashboard from 'src/features/accountingRanges/components/AccountingRangeDashboard';
import SubscriptionDashboard from 'src/features/subscriptions/components/SubscriptionDashboard';
import SubscriptionItemDashboard from 'src/features/subscriptionItems/components/SubscriptionItemDashboard';
import TabFallback from 'src/features/home/routes/TabFallback';
import SubscriptionItemPricingOverridesTable from 'src/features/subscriptionItemPricingOverrides/components/tables/SubscriptionItemPricingOverridesTable';
import SubscriptionItemQuantityEventsTable from 'src/features/subscriptionItemQuantityEvents/components/tables/SubscriptionItemQuantityEventsTable';
import SubscriptionItemUsageRecordTable from 'src/features/subscriptionItemUsageRecords/components/tables/SubscriptionItemUsageRecordTable';
import PaymentDashboard from 'src/features/payments/components/PaymentDashboard';
import FinancialDocumentDashboard from 'src/features/financialDocuments/components/FinancialDocumentDashboard';
import UnitsTable from 'src/features/units/components/tables/UnitsTable';
import PaymentsLayout from 'src/features/payments/components/PaymentsLayout';
import SubscriptionLayout from 'src/features/subscriptions/components/SubscriptionLayout';
import FinancialDocumentsLayout from 'src/features/financialDocuments/components/FinancialDocumentsLayout';
import PaymentMethodsLayout from 'src/features/paymentMethods/components/PaymentMethodsLayout';
import ChargesLayout from 'src/features/charges/components/ChargesLayout';
import BillingRunsLayout from 'src/features/billingRuns/components/BillingRunsLayout';
import BillingRunDashboard from 'src/features/billingRuns/components/BillingRunDashboard';
import BillingAttemptsLayout from 'src/features/billingAttempts/components/BillingAttemptsLayout';
import DiscountPoliciesLayout from 'src/features/discountPolicies/components/DiscountPoliciesLayout';
import DiscountPolicyDashboard from 'src/features/discountPolicies/components/DiscountPolicyDashboard';
import VoucherTypesLayout from 'src/features/voucherTypes/components/VoucherTypesLayout';
import VoucherTypeDashboard from 'src/features/voucherTypes/components/VoucherTypeDashboard';
import VouchersLayout from 'src/features/vouchers/components/VouchersLayout';
import VoucherDashboard from 'src/features/vouchers/components/VoucherDashboard';
import DiscountLayout from 'src/features/discounts/components/DiscountLayout';
import DiscountDashboard from 'src/features/discounts/components/DiscountDashboard';
import BillingEntityBankAccountsLayout from 'src/features/billingEntityBankAccounts/components/BillingEntityBankAccountsLayout';
import MoneyAccountsLayout from 'src/features/moneyAccounts/components/MoneyAccountsLayout';
import WebhookEndpointsLayout from 'src/features/webhook-endpoints/components/WebhookEndpointsLayout';
import WebhookEventsLayout from 'src/features/webhook-events/components/WebhookEventsLayout';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { useIntl } from 'react-intl';
import { generalMessages } from 'src/utils/messages';
import RolesTable from 'src/features/roles/components/tables/RolesTable';
import RoleDashboard from 'src/features/roles/components/RoleDashboard';
import UsersLayout from 'src/features/users/components/UsersLayout';
import ProfileRoutes from 'src/features/profile/routes';

// Tab panels. Each dashboard's tabs are child routes, so the tables below mount into the
// dashboard's <Outlet /> rather than being rendered by the dashboard directly.
import SubscriptionsTable from 'src/features/subscriptions/components/tables/SubscriptionsTable';
import FinancialDocumentsTable from 'src/features/financialDocuments/components/tables/FinancialDocumentsTable';
import ChargesTable from 'src/features/charges/components/tables/ChargesTable';
import PaymentsTable from 'src/features/payments/components/tables/PaymentsTable';
import CustomerPaymentMethodsTable from 'src/features/paymentMethods/components/tables/CustomerPaymentMethodsTable';
import VouchersTable from 'src/features/vouchers/components/tables/VouchersTable';
import SubscriptionItemsTable from 'src/features/subscriptionItems/components/tables/SubscriptionItemsTable';
import DiscountsTable from 'src/features/discounts/components/tables/DiscountsTable';
import SubscriptionPaymentsTab from 'src/features/subscriptions/components/tabs/SubscriptionPaymentsTab';
import ServicePlanItemsTab from 'src/features/servicePlans/components/tabs/ServicePlanItemsTab';
import ServicePlanTermsTab from 'src/features/servicePlans/components/tabs/ServicePlanTermsTab';
import ServicePlanBillingPoliciesTab from 'src/features/servicePlanItems/components/tabs/ServicePlanBillingPoliciesTab';
import ServicePlanPricingPoliciesTab from 'src/features/servicePlanItems/components/tabs/ServicePlanPricingPoliciesTab';
import FinancialDocumentRowsTab from 'src/features/financialDocuments/components/tabs/FinancialDocumentRowsTab';
import FinancialDocumentAllocationsTab from 'src/features/financialDocuments/components/tabs/FinancialDocumentAllocationsTab';
import MoneyAccountsTable from 'src/features/moneyAccounts/components/tables/MoneyAccountsTable';
import BillingEntityBankAccountsTable from 'src/features/billingEntityBankAccounts/components/tables/BillingEntityBankAccountsTable';

function HomeRoutes() {
    const { setForceLeftStaticPanel } = useSidePanel();
    const { formatMessage } = useIntl();

    React.useEffect(() => {
        setForceLeftStaticPanel(true);
        return () => setForceLeftStaticPanel(false);
    }, [setForceLeftStaticPanel]);

    const permissionAccessCustomers = useHasPermissions([PERMISSION.ACCESS_CUSTOMERS]);
    const permissionPayments = useHasPermissions([PERMISSION.ACCESS_PAYMENTS]);
    const permissionSubscriptions = useHasPermissions([PERMISSION.ACCESS_SUBSCRIPTIONS]);
    const permissionTaxReasons = useHasPermissions([PERMISSION.ACCESS_TAX_REASONS]);
    const permissionServicePlans = useHasPermissions([PERMISSION.ACCESS_SERVICE_PLANS]);
    const permissionDiscounts = useHasPermissions([PERMISSION.ACCESS_DISCOUNTS]);
    const permissionVouchers = useHasPermissions([PERMISSION.ACCESS_VOUCHERS]);
    const permissionCharges = useHasPermissions([PERMISSION.ACCESS_CHARGES]);
    const permissionFinancialDocuments = useHasPermissions([PERMISSION.ACCESS_FINANCIAL_DOCUMENTS]);
    const permissionBillingEntities = useHasPermissions([PERMISSION.ACCESS_BILLING_ENTITIES]);
    const permissionAccountingHeadings = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_HEADINGS]);
    const permissionAccountingRanges = useHasPermissions([PERMISSION.ACCESS_ACCOUNTING_RANGES]);
    const permissionUnits = useHasPermissions([PERMISSION.ACCESS_UNITS]);
    const permissionMoneyAccounts = useHasPermissions([PERMISSION.ACCESS_MONEY_ACCOUNTS]);
    const permissionAccessRoles = useHasPermissions([PERMISSION.ACCESS_ROLES]);
    const permissionAccessUsers = useHasPermissions([PERMISSION.ACCESS_USERS]);

    const defaultRoutePath = (() => {
        if (permissionAccessCustomers) return '/customers';
        if (permissionAccessCustomers) return '/payment-methods';
        if (permissionAccessUsers) return '/users';
        if (permissionAccessRoles) return '/roles';
        if (permissionSubscriptions) return '/subscriptions';
        if (permissionTaxReasons) return '/tax-reasons';
        if (permissionServicePlans) return '/service-plan-categories';
        if (permissionDiscounts) return '/discount-policies';
        if (permissionVouchers) return '/vouchers';
        if (permissionCharges) return '/charges';
        if (permissionPayments) return '/payments';
        if (permissionFinancialDocuments) return '/financial-documents';
        if (permissionBillingEntities) return '/billing-entity';
        if (permissionMoneyAccounts) return '/money-accounts';
        if (permissionAccountingHeadings) return '/accounting-headers';
        if (permissionAccountingRanges) return '/accounting-ranges';
        if (permissionUnits) return '/units';

        return '/no-access';
    })();

    // Each dashboard's tabs are routes, so its index has to redirect somewhere. The target is the
    // first tab the user is allowed to see — the same precedence the tab rows render in.
    const defaultCustomerTab = (() => {
        if (permissionSubscriptions) return 'subscriptions';
        if (permissionFinancialDocuments) return 'financial-documents';
        if (permissionCharges) return 'charges';
        if (permissionPayments) return 'payments';
        return 'vouchers';
    })();

    const defaultSubscriptionTab = (() => {
        if (permissionSubscriptions) return 'subscription-items';
        if (permissionCharges) return 'charges';
        if (permissionFinancialDocuments) return 'financial-documents';
        if (permissionPayments) return 'payments';
        return 'discounts';
    })();

    const defaultBillingEntityTab = permissionMoneyAccounts ? 'money-accounts' : 'bank-accounts';

    return (
        <Routes>
            <Route element={<HomeLayout />}>
                <Route index element={<Navigate to={defaultRoutePath} replace />} />
                {permissionAccessCustomers && (
                    <>
                        <Route path="customers" element={<CustomersTable />} />
                        <Route path="customers/:customerId" element={<CustomerDashboard />}>
                            <Route index element={<Navigate to={defaultCustomerTab} replace />} />
                            {permissionSubscriptions && <Route path="subscriptions" element={<SubscriptionsTable />} />}
                            {permissionFinancialDocuments && (
                                <Route path="financial-documents" element={<FinancialDocumentsTable />} />
                            )}
                            {permissionCharges && <Route path="charges" element={<ChargesTable />} />}
                            {permissionPayments && (
                                <>
                                    <Route path="payments" element={<PaymentsTable />} />
                                    <Route path="payment-methods" element={<CustomerPaymentMethodsTable />} />
                                </>
                            )}
                            {permissionVouchers && <Route path="vouchers" element={<VouchersTable />} />}
                            {/*
                             * An unknown tab, or one the user has lost permission for, lands on the
                             * default tab rather than rendering an empty panel.
                             */}
                            <Route path="*" element={<TabFallback to={defaultCustomerTab} />} />
                        </Route>
                        <Route path="payment-methods" element={<PaymentMethodsLayout />} />
                    </>
                )}
                {permissionAccessUsers && <Route path="users" element={<UsersLayout />} />}
                {permissionAccessRoles && (
                    <>
                        <Route path="roles" element={<RolesTable />} />
                        <Route path="roles/:roleId" element={<RoleDashboard />} />
                    </>
                )}
                {permissionSubscriptions && (
                    <>
                        <Route path="subscriptions" element={<SubscriptionLayout />} />
                        <Route
                            path="subscriptions/:subscriptionId/subscription-item/:subscriptionItemId"
                            element={<SubscriptionItemDashboard />}
                        >
                            {/*
                             * Which panels apply depends on the item's plan configuration, which
                             * is only known once it has loaded — SubscriptionItemDashboardTables
                             * redirects to the first applicable tab, so there is no static index.
                             */}
                            <Route
                                path="pricing-overrides"
                                element={<SubscriptionItemPricingOverridesTable />}
                            />
                            <Route path="quantity-events" element={<SubscriptionItemQuantityEventsTable />} />
                            <Route path="usage-records" element={<SubscriptionItemUsageRecordTable />} />
                        </Route>
                        <Route path="subscriptions/:subscriptionId" element={<SubscriptionDashboard />}>
                            <Route index element={<Navigate to={defaultSubscriptionTab} replace />} />
                            <Route path="subscription-items" element={<SubscriptionItemsTable />} />
                            {permissionCharges && <Route path="charges" element={<ChargesTable />} />}
                            {permissionFinancialDocuments && (
                                <Route path="financial-documents" element={<FinancialDocumentsTable />} />
                            )}
                            {permissionPayments && <Route path="payments" element={<SubscriptionPaymentsTab />} />}
                            {permissionDiscounts && <Route path="discounts" element={<DiscountsTable />} />}
                            {/*
                             * An unknown tab, or one the user has lost permission for, lands on the
                             * default tab rather than rendering an empty panel.
                             */}
                            <Route path="*" element={<TabFallback to={defaultSubscriptionTab} />} />
                        </Route>
                    </>
                )}

                {permissionCharges && (
                    <>
                        <Route path="charges" element={<ChargesLayout />} />
                        <Route path="billing-runs" element={<BillingRunsLayout />} />
                        <Route path="billing-runs/:billingRunId" element={<BillingRunDashboard />} />
                        <Route path="billing-attempts" element={<BillingAttemptsLayout />} />
                    </>
                )}
                {permissionPayments && (
                    <>
                        <Route path="payments" element={<PaymentsLayout />} />
                        <Route path="payments/:paymentId" element={<PaymentDashboard />} />
                    </>
                )}

                {permissionFinancialDocuments && (
                    <>
                        <Route path="financial-documents" element={<FinancialDocumentsLayout />} />
                        <Route path="financial-documents/:financialDocumentId" element={<FinancialDocumentDashboard />}>
                            <Route index element={<Navigate to="rows" replace />} />
                            <Route path="rows" element={<FinancialDocumentRowsTab />} />
                            {permissionPayments && (
                                <Route path="allocations" element={<FinancialDocumentAllocationsTab />} />
                            )}
                            <Route path="*" element={<TabFallback to="rows" />} />
                        </Route>
                    </>
                )}
                {permissionTaxReasons && <Route path="tax-reasons" element={<TaxReasonsTable />} />}

                {permissionServicePlans && (
                    <>
                        <Route path="service-plan-categories" element={<ServicePlanCategoriesTable />} />
                        <Route path="service-plans" element={<ServicePlansTable />} />
                        <Route
                            path="service-plans/:servicePlanId/simulate-billing"
                            element={<BillingSimulationPage />}
                        />
                        <Route path="service-plans/:servicePlanId" element={<ServicePlanDashboard />}>
                            <Route index element={<Navigate to="items" replace />} />
                            <Route path="items" element={<ServicePlanItemsTab />} />
                            <Route path="terms" element={<ServicePlanTermsTab />} />
                            <Route path="*" element={<TabFallback to="items" />} />
                        </Route>
                        <Route path="service-plan-item/:servicePlanItemId" element={<ServicePlanItemDashboard />}>
                            <Route index element={<Navigate to="billing-policies" replace />} />
                            <Route path="billing-policies" element={<ServicePlanBillingPoliciesTab />} />
                            <Route path="pricing-policies" element={<ServicePlanPricingPoliciesTab />} />
                            <Route path="*" element={<TabFallback to="billing-policies" />} />
                        </Route>
                        <Route path="reference-rates" element={<ReferenceRatesLayout />} />
                        <Route path="reference-rates/:referenceRateId" element={<ReferenceRateDashboard />} />
                        <Route path="billable-metrics" element={<BillableMetricsLayout />} />
                    </>
                )}

                {permissionBillingEntities && (
                    <>
                        <Route path="billing-entity" element={<BillingEntitiesTable />} />
                        <Route path="billing-entity/:entityId" element={<BillingEntityDashboard />}>
                            <Route index element={<Navigate to={defaultBillingEntityTab} replace />} />
                            {permissionMoneyAccounts && (
                                <Route path="money-accounts" element={<MoneyAccountsTable />} />
                            )}
                            <Route path="bank-accounts" element={<BillingEntityBankAccountsTable />} />
                            <Route path="*" element={<TabFallback to={defaultBillingEntityTab} />} />
                        </Route>
                        <Route path="bank-accounts" element={<BillingEntityBankAccountsLayout />} />
                    </>
                )}

                {permissionMoneyAccounts && <Route path="money-accounts" element={<MoneyAccountsLayout />} />}

                {permissionAccountingHeadings && (
                    <Route path="accounting-headers" element={<AccountingHeadersTable />} />
                )}

                {permissionAccountingRanges && (
                    <>
                        <Route path="accounting-ranges" element={<AccountingRangesTable />} />
                        <Route path="accounting-ranges/:rangeId" element={<AccountingRangeDashboard />} />
                    </>
                )}

                {permissionUnits && <Route path="units" element={<UnitsTable />} />}

                {permissionDiscounts && (
                    <>
                        <Route path="discount-policies" element={<DiscountPoliciesLayout />} />
                        <Route path="discount-policies/:discountPolicyId" element={<DiscountPolicyDashboard />} />
                        <Route path="discounts" element={<DiscountLayout />} />
                        <Route path="discounts/:discountId" element={<DiscountDashboard />} />
                    </>
                )}

                {permissionVouchers && (
                    <>
                        <Route path="voucher-types" element={<VoucherTypesLayout />} />
                        <Route path="voucher-types/:voucherTypeId" element={<VoucherTypeDashboard />} />
                        <Route path="vouchers" element={<VouchersLayout />} />
                        <Route path="vouchers/:voucherId" element={<VoucherDashboard />} />
                    </>
                )}

                <Route path="webhook-endpoints" element={<WebhookEndpointsLayout />} />
                <Route path="webhook-events" element={<WebhookEventsLayout />} />

                <Route path="profile/*" element={<ProfileRoutes />} />

                <Route
                    path="/no-access"
                    element={
                        <div className="flex h-full items-center justify-center">
                            {formatMessage(generalMessages.noAccess)}
                        </div>
                    }
                />
            </Route>
        </Routes>
    );
}

export default HomeRoutes;
